// src/context/AuthCtx.jsx
import React, { createContext, useCallback, useState, useEffect } from "react";
import { whoAmI, refreshSession, setAccessToken, clearAccessToken, setClientBillingGuard } from "../services/api";

export const AuthCtx = createContext({
  jwt: "",
  role: "user",
  acl: { db: [], company: {}, modules: [] },
  dbInfo: {},
  companyInfo: {},
  tenants: [],
  tenantMeta: null,
  setJwt: () => {},
  refreshAuth: () => {},
  authReady: false,
});

export function AuthProvider({ children }) {
  const [jwt, setJwtState] = useState("");
  const [role, setRole] = useState("user");
  const [acl, setAcl] = useState({ db: [], company: {}, modules: [] });
  const [dbInfo, setDbInfo] = useState({});
  const [companyInfo, setCompanyInfo] = useState({});
  const [tenants, setTenants] = useState([]);
  const [tenantMeta, setTenantMeta] = useState(null);
  const [authReady, setAuthReady] = useState(false);
  const [bootstrapped, setBootstrapped] = useState(false);
  const isLocalhost =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1");
  const pathname = typeof window !== "undefined" ? window.location.pathname : "";
  const isLocalPublicRoute =
    isLocalhost &&
    (pathname === "/" ||
      pathname === "/login" ||
      pathname === "/sign-up" ||
      pathname === "/demo" ||
      pathname.startsWith("/prototype") ||
      pathname.startsWith("/landing-preview"));

  const syncBillingGuard = useCallback((meta, userRole = "") => {
    const billing = meta?.billing || null;
    const state = String(billing?.state || "").toLowerCase();
    const currentEndMs = billing?.current_end_at ? new Date(billing.current_end_at).getTime() : null;
    const paidThroughActive = Number.isFinite(currentEndMs) && currentEndMs > Date.now();
    const licenseExpiredAt = meta?.license_expires_at ? new Date(meta.license_expires_at).getTime() : null;
    const licenseExpired = Number.isFinite(licenseExpiredAt) && licenseExpiredAt <= Date.now();
    const isSuper = String(userRole || "").toLowerCase() === "super";
    const locked =
      !isSuper &&
      (["trial_expired", "subscription_required", "expired", "completed"].includes(state) ||
        (state === "halted" && !paidThroughActive) ||
        (!billing && licenseExpired));
    let reason = "SUBSCRIPTION_REQUIRED";
    if (state === "trial_expired") reason = "TRIAL_EXPIRED";
    if (state === "halted" && !paidThroughActive) reason = "SUBSCRIPTION_HALTED";
    if (state === "expired" || state === "completed") reason = "SUBSCRIPTION_EXPIRED";
    setClientBillingGuard({ locked, reason, billing, role: userRole });
  }, []);

  /** fetch /whoami and populate all state slices */
  const refreshAuth = useCallback(async () => {
    if (!jwt) {
      setRole("user");
      setAcl({ db: [], company: {}, modules: [] });
      setDbInfo({});
      setCompanyInfo({});
      setTenants([]);
      setTenantMeta(null);
      setClientBillingGuard({ locked: false, reason: "", billing: null });
      setAuthReady(true);
      return;
    }

    setAuthReady(false);
    const { role, acl, db_info, company_info, tenants: tlist = [], tenant: tmeta = null } = await whoAmI();
    setRole(role);
    setAcl(acl);
    setDbInfo(db_info || {});
    setCompanyInfo(company_info || {});
    setTenants(Array.isArray(tlist) ? tlist : []);
    setTenantMeta(tmeta);
    syncBillingGuard(tmeta, role);
    setAuthReady(true);
  }, [jwt, syncBillingGuard]);

  /* bootstrap session using refresh cookie */
  useEffect(() => {
    if (bootstrapped) return;
    (async () => {
      if (isLocalPublicRoute) {
        clearAccessToken();
        setJwtState("");
        setClientBillingGuard({ locked: false, reason: "", billing: null });
        setAuthReady(true);
        setBootstrapped(true);
        return;
      }

      try {
        // Try to mint a new access token from the refresh cookie on page load.
        const data = await refreshSession();
        const tok = data?.access_token || "";
        if (tok) {
          setAccessToken(tok);
          setJwtState(tok);
          setAuthReady(false);
        } else {
          setAuthReady(true);
        }
      } catch {
        clearAccessToken();
        setJwtState("");
        setAuthReady(true);
      } finally {
        setBootstrapped(true);
      }
    })();
  }, [isLocalPublicRoute, bootstrapped]);

  /* refresh whoami whenever access token changes (login/switch-tenant) */
  useEffect(() => {
    if (!bootstrapped) return;
    (async () => {
      try {
        await refreshAuth();
      } catch {
        clearAccessToken();
        setJwtState("");
        setClientBillingGuard({ locked: false, reason: "", billing: null });
        setAuthReady(true);
      }
    })();
  }, [bootstrapped, jwt]);

  return (
    <AuthCtx.Provider
      value={{
        jwt,
        role,
        acl,
        dbInfo,
        companyInfo,
        tenants,
        tenantMeta,
        setJwt: (tok) => {
          setAccessToken(tok);
          setJwtState(tok);
          if (tok) {
            // Avoid a brief "ready" state with empty ACLs right after login.
            setAuthReady(false);
          }
        },
        refreshAuth,
        authReady,
      }}
    >
      {children}
    </AuthCtx.Provider>
  );
}
